import {
  X as CloseIcon,
  MessagesSquare,
  Sparkles,
} from "@tamagui/lucide-icons";
import type { ReactNode } from "react";
import { Platform } from "react-native";

import { Adapt, Dialog, Sheet, XStack, YStack, useMedia } from "@/components";
import { Button } from "@/components/Button";
import { Footer, Header } from "@/components/Layout";
import { H1, H2, Paragraph } from "@/components/Typography";
import { SMS_CONSENT_PRIVACY_DISCLOSURE } from "@/constants";

interface SmsConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConsent: () => void;
  onDecline: () => void;
  isLoading: boolean;
  title?: ReactNode;
  description?: ReactNode;
  showConsentFeatures?: boolean;
}

export default function SmsConsentDialog({
  open,
  onOpenChange,
  onConsent,
  onDecline,
  isLoading,
  title = "Receive treatment over SMS",
  description = "You can now text Curai Health whenever, wherever. No need to visit the website or download an app.",
  showConsentFeatures = true,
}: SmsConsentDialogProps) {
  const { lg } = useMedia();

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange}>
      <Dialog.Adapt when={!lg}>
        <Sheet
          modal
          dismissOnSnapToBottom
          snapPointsMode="fit"
          moveOnKeyboardChange
        >
          <Sheet.Frame>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            style={{
              backdropFilter: "blur(8px)",
            }}
          />
        </Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          style={Platform.select({
            web: {
              backdropFilter: "blur(8px)",
            },
          })}
        />
        <Dialog.FocusScope trapped loop focusOnIdle={300}>
          <Dialog.Content padding={0} width={500}>
            <YStack
              padding="$200"
              width="100%"
              height="100%"
              gap="$100"
              borderTopLeftRadius="$150"
              borderTopRightRadius="$150"
              borderBottomLeftRadius="$150"
              borderBottomRightRadius="$150"
            >
              <XStack
                justifyContent="flex-end"
                alignItems="center"
                marginBottom="$100"
              >
                <Dialog.Close asChild displayWhenAdapted>
                  <Button.Wrapper aria-label="Close">
                    <CloseIcon />
                  </Button.Wrapper>
                </Dialog.Close>
              </XStack>
              <YStack gap="$200">
                <Header>
                  <Dialog.Title asChild>
                    <H1 size="$lg" $lg={{ size: "$lg" }}>
                      {title}
                    </H1>
                  </Dialog.Title>
                </Header>
                {typeof description === "string" ? (
                  <Paragraph>{description}</Paragraph>
                ) : (
                  description
                )}
                {showConsentFeatures && (
                  <>
                    <Header>
                      <H2 size="$md" $lg={{ size: "$md" }}>
                        What will happen if you consent?
                      </H2>
                    </Header>
                    <XStack gap="$150" alignItems="flex-start">
                      <Sparkles
                        size="$300"
                        color="$textSecondary"
                        flexShrink={0}
                      />
                      <Paragraph size="$sm">
                        You will be able to text us directly with any health
                        questions or concerns
                      </Paragraph>
                    </XStack>
                    <XStack gap="$150" alignItems="flex-start">
                      <MessagesSquare
                        size="$300"
                        color="$textSecondary"
                        flexShrink={0}
                      />
                      <Paragraph size="$sm">
                        You will receive full clinical messages over text
                        (rather than a link back to the platform) that may
                        include personal health information
                      </Paragraph>
                    </XStack>
                  </>
                )}
                <Footer marginTop="$300">
                  <Button
                    mode="primary"
                    loading={isLoading}
                    onPress={onConsent}
                    disabled={isLoading}
                  >
                    I consent to receive treatment over SMS
                  </Button>
                  <Button
                    mode="tertiary"
                    onPress={onDecline}
                    disabled={isLoading}
                  >
                    I do not consent
                  </Button>
                </Footer>
                <Paragraph size="$xs" fontStyle="italic">
                  {SMS_CONSENT_PRIVACY_DISCLOSURE}
                </Paragraph>
              </YStack>
            </YStack>
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
